import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [invoiceToken, setInvoiceToken] = useState(null);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let attempts = 0;
    let active = true;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (!active) return;
        setInvoiceToken(data.invoice_token);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setState("error"); return; }
        if (attempts >= 8) { setState("pending"); return; }
        attempts += 1;
        setTimeout(poll, 2000);
      } catch {
        if (active) setState("error");
      }
    };
    poll();
    return () => { active = false; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-ink text-white font-body flex items-center justify-center px-5">
      <div className="max-w-md text-center" data-testid="payment-result">
        <span className="bg-white rounded-md p-1.5 inline-block mb-8"><img src="/logo-white.png" alt="B3 Contracting LLC" className="h-16 w-auto object-contain" /></span>

        {state === "checking" && (
          <div className="flex flex-col items-center gap-4">
            <Loader2 size={44} className="animate-spin text-gold" />
            <p className="text-white/70">Confirming your payment…</p>
          </div>
        )}

        {state === "paid" && (
          <div className="flex flex-col items-center gap-4">
            <CheckCircle2 size={64} className="text-green-400" />
            <h1 className="font-heading font-bold text-3xl tracking-tight">Payment successful</h1>
            <p className="text-white/70">Thank you! A receipt is on its way to your email. B3 Contracting will be in touch about your project.</p>
          </div>
        )}

        {state === "pending" && (
          <div className="flex flex-col items-center gap-4">
            <Loader2 size={44} className="animate-spin text-gold" />
            <h1 className="font-heading font-bold text-2xl tracking-tight">Payment processing</h1>
            <p className="text-white/70">Your payment is being processed. You'll receive an email receipt once it clears.</p>
          </div>
        )}

        {state === "error" && (
          <div className="flex flex-col items-center gap-4">
            <h1 className="font-heading font-bold text-2xl tracking-tight">We couldn't confirm this payment</h1>
            <p className="text-white/70">If you were charged, you'll still get a receipt by email. Otherwise please try again.</p>
          </div>
        )}

        <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
          {invoiceToken && <a href={`/invoice/${invoiceToken}`} className="border border-white/25 text-white px-6 py-3 hover:border-gold hover:text-gold transition-colors">View invoice</a>}
          <a href="/" className="text-white/50 px-6 py-3 hover:text-gold transition-colors">Back to website</a>
        </div>
      </div>
    </div>
  );
}
